import moment from 'moment';
import { ICS2CodelistsMap } from 'store/codelists/code';
import {
    ICS2NotificationBody,
    readNotificationIssueDate,
    readNotificationMrn,
} from 'store/declarations/eu/ics2/ics2-notification';
import useIcs2CodelistLabel from 'hooks/useIcs2CodelistLabel';
import DetailsHeader from 'views/declarations/common/ics2-message-details/DetailsHeader';
import MessageDetails from 'views/declarations/common/ics2-message-details/MessageDetails';

const DATE_FORMAT = 'DD/MM/YYYY, HH:mm';

type Props = {
    notification?: ICS2NotificationBody;
    onBack: () => void;
};

const IE3Q01Details = ({ notification, onBack }: Props) => {
    const getLabel = useIcs2CodelistLabel([ICS2CodelistsMap.CL754]);

    const mrn = readNotificationMrn(notification);
    const issuedAt = readNotificationIssueDate(notification);
    const details = notification?.doNotLoadDetails;

    const describeDocument = (document?: { documentNumber?: string; type?: string } | null) =>
        document?.documentNumber
            ? [document.documentNumber, document.type && getLabel(document.type, ICS2CodelistsMap.CL754)]
                  .filter(Boolean)
                  .join(' - ')
            : undefined;

    const affected = [
        details?.transportDocument?.documentNumber &&
            `House transport document ${describeDocument(details.transportDocument)}`,
        ...(details?.receptacle ?? []).map(
            (entry) => entry.receptacleIdentificationNumber && `Receptacle ${entry.receptacleIdentificationNumber}`
        ),
        ...(details?.transportEquipment ?? []).map(
            (entry) => entry.containerIdentificationNumber && `Container ${entry.containerIdentificationNumber}`
        ),
    ].filter(Boolean) as string[];

    const masterDocument = describeDocument(notification?.transportDocument);
    const houseDocument = describeDocument(notification?.transportDocumentHouse);

    const metadata: Record<string, string> = {
        Code: 'IE3Q01',
        Message: 'Do Not Load (DNL)',
        ...(mrn ? { MRN: mrn } : {}),
        ...(masterDocument ? { 'Master Transport Document': masterDocument } : {}),
        ...(houseDocument ? { 'House Transport Document': houseDocument } : {}),
        'Affected Consignments': affected.length ? affected.join(', ') : 'All goods on this ENS filing',
        ...(notification?.responsibleMemberState?.country
            ? { 'Issued By': `Customs of ${notification.responsibleMemberState.country}` }
            : {}),
        ...(issuedAt ? { 'Issued At': moment(issuedAt).format(DATE_FORMAT) } : {}),
    };

    return (
        <>
            <DetailsHeader onBack={onBack} />

            <MessageDetails
                messageMetadata={metadata}
                details={{
                    whatHappened:
                        'Customs authorities have issued a Do Not Load (DNL) order for one or more consignments in your ENS. These goods must not be loaded onto the means of transport.',
                    whyItHappened:
                        'An immediate serious safety or security risk has been identified during the risk assessment. Under Article 186 UCC Implementing Act, customs authorities have the power to prevent loading of goods that pose a threat.',
                    nextSteps: (
                        <ul style={{ marginBottom: 0, listStyleType: 'initial' }}>
                            <li>
                                Stop loading immediately. Do not load the identified consignment(s) onto the
                                aircraft/vessel.
                            </li>
                            <li>Inform the carrier immediately - they have also received this notification.</li>
                            <li>Do not attempt to amend the ENS while a DNL is active - amendments are blocked.</li>
                            <li>
                                A Risk Assessment Complete notification (IE3N03) will not be issued for consignments
                                under a DNL.
                            </li>
                            <li>Contact the issuing customs authority for further instructions.</li>
                        </ul>
                    ),
                }}
            />
        </>
    );
};

export default IE3Q01Details;
